import * as React from 'react';
import {useState, useEffect} from 'react';
import {Create, SimpleForm, TextInput, BooleanInput, SelectInput, required, email, minLength, regex} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Box, Grid, FormControlLabel, Switch} from '@mui/material';
import {normalizeCreateUserPayload} from './user.create.utils';

const createValidationKeys = {
  required: 'resources.users.views.create.validation.required',
  emailInvalid: 'resources.users.views.create.validation.emailInvalid',
  phoneInvalid: 'resources.users.views.create.validation.phoneInvalid',
  passwordMinLength: 'resources.users.views.create.validation.passwordMinLength',
};

const validatePhone = regex(/^((((0{2}?)|(\+){1})46)|0)7[\d]{8}/, createValidationKeys.phoneInvalid);

const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const UserCreateForm = () => {
  const [isSso, setIsSso] = useState(false);
  const {setValue, resetField, clearErrors, trigger} = useFormContext();

  useEffect(() => {
    setValue('__isSso', false, {shouldDirty: false});
  }, [setValue]);

  const handleSsoChange = (checked: boolean) => {
    setIsSso(checked);
    setValue('__isSso', checked, {shouldDirty: true});

    if (checked) {
      setValue('password', `SSO-${generateGuid()}`, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors('password');
      return;
    }

    resetField('password', {defaultValue: ''});
    setValue('password', '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    void trigger('password');
  };

  return (
    <>
      <Box display="flex" mb={2}>
        <BooleanInput source="isActive" defaultValue={true} />
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isSso}
                onChange={(e) => handleSsoChange(e.target.checked)}
              />
            }
            label="SSO"
            sx={{ marginLeft: '16px' }}
          />
          <Box sx={{ height: '20px' }}></Box>
        </Box>
      </Box>

        <Grid container width="100%" spacing={1}>
          <Grid item xs={8}>
            <Box display="flex">
              <Box flex={1} mr={1}>
                <TextInput source="firstName" fullWidth validate={[required(createValidationKeys.required)]} />
              </Box>
              <Box flex={1} ml={1} mr={1}>
                <TextInput source="lastName" fullWidth validate={[required(createValidationKeys.required)]} />
              </Box>
              <Box flex={1} ml={1}>
                <SelectInput
                  source="role"
                  fullWidth
                  validate={[required(createValidationKeys.required)]}
                  choices={[
                    {id: 'admin', name: 'resources.users.fields.admin'},
                    {id: 'permitSupervisor', name: 'resources.users.fields.permitSupervisor'},
                    {id: 'productionOperator', name: 'resources.users.fields.productionOperator'},
                    {id: 'maintenanceTechnician', name: 'resources.users.fields.maintenanceTechnician'},
                  ]}
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={2} mr={1}>
                <TextInput source="phone" fullWidth validate={[required(createValidationKeys.required), validatePhone]} />
              </Box>
              <Box flex={1}></Box>
            </Box>
          </Grid>

          <Grid item xs={8}>
            <Box display="flex">
              <Box flex={1} mr={1}>
                <TextInput
                  source="email"
                  fullWidth
                  validate={[required(createValidationKeys.required), email(createValidationKeys.emailInvalid)]}
                />
              </Box>
              <Box flex={1} ml={1}>
                <TextInput 
                  source="password" 
                  fullWidth 
                  type="password"
                  validate={
                    !isSso
                      ? [required(createValidationKeys.required), minLength(6, createValidationKeys.passwordMinLength)]
                      : []
                  }
                  InputProps={{ readOnly: isSso }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: isSso ? 'action.disabledBackground' : 'background.paper',
                    },
                    '& .MuiInputBase-input': {
                      color: isSso ? 'text.disabled' : 'text.primary',
                    },
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    );
};

export const UserCreate = () => {
  const transform = (data: Record<string, any>) =>
    normalizeCreateUserPayload(data, () => `SSO-${generateGuid()}`);

  return (
    <Create
      redirect="list"
      transform={transform}
      children={
        <SimpleForm children={<UserCreateForm />} />
      }
    />
  );
};
